import { parseItemInput } from "../parser/item-input.js";
import { splitItemInput } from "../parser/split-input.js";
import type { AiResult } from "./schema.js";

/**
 * Der Weg ohne Modell.
 *
 * Gemessen an der echten Nutzung (Tabelle ai_usage) braucht das Zuhören eine
 * halbe Sekunde und das Verstehen sieben. Das lohnt sich für "Streich die
 * Milch von der Wochenliste und setz Butter drauf", aber der weitaus häufigste
 * Satz im Laden ist eine Aufzählung: "Milch, Butter und sechs Eier". Dafür
 * genügt der Parser, den die App ohnehin für die Tastatureingabe hat.
 *
 * Die Regel ist bewusst streng und sagt im Zweifel nein: Ein falsch erkannter
 * Befehl kostet Vertrauen, eine Sekunde Wartezeit nicht. Was hier durchfällt,
 * geht wie bisher an das Modell. Und weil das Ergebnis danach durch dieselbe
 * Vorschau läuft, sieht man in beiden Fällen vor dem Übernehmen, was passiert.
 */

/** Wörter, die auf etwas anderes als eine Aufzählung hindeuten. */
const BEFEHLSWOERTER = [
  "streich",
  "lösch",
  "losch",
  "entfern",
  "nimm",
  "weg",
  "hak",
  "abhaken",
  "erledigt",
  "gekauft",
  "setz",
  "verschieb",
  "ändere",
  "andere ",
  "benenn",
  "leere",
  "leer die",
  "neue liste",
  "neue einkaufsliste",
  "welche",
  "was ",
  "wie viel",
  "wieviel",
  "zeig",
  "sag mir",
  "steht auf",
  "brauchen wir",
  "haben wir",
  "liste",
  "rezept",
  "plan",
];

/** Höflichkeiten und Einleitungen, die vorne wegdürfen. */
const EINLEITUNGEN = [
  "bitte",
  "ich brauche",
  "ich bräuchte",
  "wir brauchen",
  "ich hätte gern",
  "kauf noch",
  "kaufe noch",
  "kauf",
  "kaufe",
  "besorg noch",
  "besorge noch",
  "besorg",
  "besorge",
  "hol noch",
  "hole noch",
  "noch",
  "dazu",
  "und",
];

/** Anhängsel, die hinten wegdürfen. */
const ANHAENGSEL = ["bitte", "dazu", "noch", "danke"];

function abschneiden(text: string, teile: readonly string[], vorne: boolean): string {
  let rest = text;
  let nochmal = true;
  while (nochmal) {
    nochmal = false;
    for (const teil of teile) {
      const passt = vorne
        ? new RegExp(`^${teil}\\b[\\s,]*`, "iu")
        : new RegExp(`[\\s,]*\\b${teil}$`, "iu");
      if (passt.test(rest)) {
        rest = rest.replace(passt, "").trim();
        nochmal = true;
      }
    }
  }
  return rest;
}

/**
 * Versucht, aus einem gesprochenen Satz ohne Modell eine reine Aufzählung zu
 * machen. Gibt null zurück, sobald irgendetwas nicht eindeutig ist.
 *
 * `listId` ist die gerade geöffnete Liste. Sie wird fest eingetragen und nicht
 * dem Kern zum Raten überlassen: Wer auf eine offene Liste spricht, meint sie
 * auch, genau wie beim Tippen in dieselbe Liste. Ohne diese Angabe würde die
 * Vorschau eine Rückfrage verlangen, und der schnelle Weg wäre wieder langsam.
 */
export function versteheLokal(text: string, listId: string | null = null): AiResult | null {
  const roh = text.trim().replace(/\s+/gu, " ").replace(/[.!?]+$/u, "");
  if (roh.length === 0 || roh.length > 120) return null;
  if (roh.includes("?")) return null;

  const klein = roh.toLocaleLowerCase("de-DE");
  if (BEFEHLSWOERTER.some((wort) => klein.includes(wort))) return null;

  const ohneRand = abschneiden(abschneiden(roh, EINLEITUNGEN, true), ANHAENGSEL, false);
  if (ohneRand.length === 0) return null;

  // Dieselbe Zerlegung wie in der Schnelleingabe. Zwei Wege, eine Regel:
  // Sonst trennt das Feld anders als das Mikrofon, und niemand versteht,
  // warum dasselbe gesagt zweimal etwas anderes ergibt.
  const teile = splitItemInput(ohneRand);
  if (teile.length === 0 || teile.length > 12) return null;

  // Hat der Zerleger sich geweigert zu trennen, obwohl ein "und" dasteht, war
  // die Eingabe nicht eindeutig. Dann lieber das Modell fragen, als etwas zu
  // erfinden.
  if (teile.some((teil) => /\bund\b/iu.test(teil))) return null;

  const items = [];
  for (const teil of teile) {
    const geparst = parseItemInput(teil);
    const name = geparst.name.trim();
    // Ein Name muss ein Wort sein, kein Rest einer Menge. Zwei Buchstaben
    // mindestens, sonst landet aus "2 x" ein Artikel namens "x" auf der Liste.
    if (name.length < 2 || !/\p{L}{2}/u.test(name)) return null;

    // Und er muss wie ein Artikelname aussehen. Ohne diese Grenze wird aus
    // "Ich bräuchte für das Abendessen am Samstag noch einiges" ein einziger
    // Artikel mit diesem Namen: Der Satz hat kein Trennzeichen, also zerfällt
    // er nicht, und der Parser findet nichts zu beanstanden. Vier Wörter
    // reichen für "Käse mittelalt gerieben", Fließtext fällt durch.
    if (name.length > 40 || name.split(" ").length > 4) return null;

    // Bleibt eine Notiz übrig, ist die Zerlegung nicht sicher: "Mandarinen,
    // äpfel" kann ein Artikel mit Beschreibung sein oder zwei Artikel. Bis zum
    // 16.10.2026 fiel die Notiz hier still weg, und von einer gesprochenen
    // Aufzählung blieb nur der erste Artikel übrig.
    if (geparst.note !== null) return null;

    // Mehrere groß geschriebene Wörter ohne Trennzeichen sind im Deutschen
    // meist mehrere Hauptwörter: "Mandarinen Äpfel Milch". Ob "Griechischer
    // Joghurt" einer ist oder zwei, entscheidet das Modell.
    if (name.split(" ").filter((wort) => /^\p{Lu}/u.test(wort)).length > 1) return null;

    items.push({
      name,
      qty: geparst.quantity,
      unit: geparst.unit,
      category: null,
      product_id: null,
      confidence: 1,
      needs_clarification: false,
      alternatives: [] as string[],
    });
  }

  return {
    actions: [{ type: "add" as const, list_id: listId, list_name: null, list_confidence: 1, items }],
    summary: items.length === 1 ? `${items[0]?.name ?? ""} hinzufügen` : `${String(items.length)} Artikel hinzufügen`,
  };
}
